import TinyColor from "@ctrl/tinycolor";
import { BrandKit } from "@redotech/redo-model/brand-kit";
import { InputElement } from "@redotech/redo-model/forge/marketing-signup-forge-types";
import { CustomFieldType } from "@redotech/redo-model/signup-form";
import { SignupForm } from "@redotech/redo-model/signup-form-schema";
import { assertNever } from "@redotech/util/type";
import * as classNames from "classnames";
import { memo, NamedExoticComponent, useContext, useState } from "react";
import { RedoRadioGroup } from "../../arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { BrandKitDateInput } from "../../brand-kit-date-input";
import {
  BrandKitPhoneNumberInput,
  BrandKitTextAreaInput,
  BrandKitTextInput,
} from "../../brand-kit-text-input";
import { ExternalLink } from "../../link";
import { Text } from "../../text";
import * as signupFormCss from "../signup-form.module.css";
import { CustomFieldContext } from "./context";
import { FormElementProps } from "./index";

const EmailInput = memo(function EmailInput({
  form,
  brandKit,
  element,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      inputTypeHint="email"
      name="email"
      override={
        form.theme.overrides.inputs.enabled
          ? form.theme.overrides.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required
      setValue={setValue}
      value={value}
    />
  );
});

const PhoneInput = memo(function PhoneInput({
  form,
  brandKit,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState("");
  return (
    <>
      <BrandKitPhoneNumberInput
        brandKit={brandKit}
        name="phone"
        override={
          form.theme.overrides.inputs.enabled
            ? form.theme.overrides.inputs
            : undefined
        }
        required
        setValue={setValue}
        value={value}
      />
      <Text
        className={classNames(
          signupFormCss.legalDisclaimer,
          TinyColor(form.layout.backgroundColor).isDark()
            ? signupFormCss.invertedTextColor
            : undefined,
        )}
        dir="column"
        mt="lg"
      >
        By providing your phone number, you agree to receive recurring automated
        marketing text messages (e.g. cart reminders) from this shop. Consent is
        not a condition to obtain goods or services. Msg & data rates may apply.
        Msg frequency varies. Reply HELP for help and STOP to cancel. View{" "}
        <ExternalLink
          showIcon={false}
          url="https://www.getredo.com/terms-conditions"
        >
          Terms of Service
        </ExternalLink>{" "}
        and{" "}
        <ExternalLink
          showIcon={false}
          url="https://www.getredo.com/privacy-policy"
        >
          Privacy Policy.
        </ExternalLink>
      </Text>
    </>
  );
});

const BirthdayInput = memo(function BirthdayInput({
  form,
  brandKit,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState<Temporal.PlainDate | null>(null);
  return (
    <BrandKitDateInput
      brandKit={brandKit}
      name="birthday"
      override={
        form.theme.overrides.inputs.enabled
          ? form.theme.overrides.inputs
          : undefined
      }
      setValue={setValue}
      value={value}
    />
  );
});

const CustomDateField = memo(function CustomDateField({
  form,
  brandKit,
  element,
}: {
  form: SignupForm;
  element: InputElement;
  brandKit: BrandKit;
}) {
  const [value, setValue] = useState<Temporal.PlainDate | null>(null);
  return (
    <BrandKitDateInput
      brandKit={brandKit}
      name={element.customField?.id}
      override={
        form.theme.overrides.inputs.enabled
          ? form.theme.overrides.inputs
          : undefined
      }
      required
      setValue={setValue}
      value={value}
    />
  );
});

const CustomDropdownField = memo(function CustomDropdownField({
  form,
  brandKit,
}: {
  form: SignupForm;
  element: InputElement;
  brandKit: BrandKit;
}) {
  const customFieldContext = useContext(CustomFieldContext);
  const [value, setValue] = useState<string | null>(null);

  return (
    <RedoSingleSelectDropdownInput
      //brandKit={brandKit} Add a brandkitsingleselectdropdowninput
      options={
        customFieldContext?.options.map((field) => ({
          value: field.id,
          text: field.name,
          id: field.id,
          type: "text",
        })) ?? []
      }
      optionSelected={(item) => setValue(item.item.value)}
      selectedItem={
        value ? { value, type: "text", text: value, id: value } : undefined
      }
      size="xs"
    />
  );
});

const CustomNumberField = memo(function CustomNumberField({
  form,
  brandKit,
  element,
}: {
  form: SignupForm;
  element: InputElement;
  brandKit: BrandKit;
}) {
  const [value, setValue] = useState<number | undefined>(undefined);
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      htmlPattern="^[0-9]*$"
      name={element.customField?.id}
      override={
        form.theme.overrides.inputs.enabled
          ? form.theme.overrides.inputs
          : undefined
      }
      required
      setValue={(value) => setValue(Number(value))}
      value={value?.toString() ?? ""}
    />
  );
});

const CustomLongTextField = memo(function CustomLongTextField({
  form,
  brandKit,
}: {
  form: SignupForm;
  element: InputElement;
  brandKit: BrandKit;
}) {
  const [value, setValue] = useState<string>("");
  return (
    <BrandKitTextAreaInput
      brandKit={brandKit}
      override={
        form.theme.overrides.inputs.enabled
          ? form.theme.overrides.inputs
          : undefined
      }
      required
      setValue={setValue}
      value={value}
    />
  );
});

const CustomTextShortField = memo(function CustomTextShortField({
  form,
  brandKit,
  element,
}: {
  form: SignupForm;
  element: InputElement;
  brandKit: BrandKit;
}) {
  const [value, setValue] = useState<string>("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      name={element.customField?.id}
      override={
        form.theme.overrides.inputs.enabled
          ? form.theme.overrides.inputs
          : undefined
      }
      required
      setValue={setValue}
      value={value}
    />
  );
});

const CustomYesNoField = memo(function CustomYesNoField({
  form,
  brandKit,
  element,
}: {
  form: SignupForm;
  element: InputElement;
  brandKit: BrandKit;
}) {
  const [value, setValue] = useState<boolean>(true);
  return (
    <RedoRadioGroup
      //brandKit={brandKit} Add a brandkitradiogroup
      options={[
        { id: true, label: "Yes" },
        { id: false, label: "No" },
      ]}
      setValue={(value) => setValue(value)}
      value={value}
    />
  );
});

const CustomInput = memo(function CustomInput({
  form,
  brandKit,
  element,
}: FormElementProps<InputElement>) {
  if (!element.customField?.fieldType) {
    return null;
  }

  switch (element.customField.fieldType) {
    case CustomFieldType.Date:
      return (
        <CustomDateField brandKit={brandKit} element={element} form={form} />
      );
    case CustomFieldType.Dropdown:
      return (
        <CustomDropdownField
          brandKit={brandKit}
          element={element}
          form={form}
        />
      );
    case CustomFieldType.Number:
      return (
        <CustomNumberField brandKit={brandKit} element={element} form={form} />
      );
    case CustomFieldType.Text:
      return (
        <CustomLongTextField
          brandKit={brandKit}
          element={element}
          form={form}
        />
      );
    case CustomFieldType.TextShort:
      return (
        <CustomTextShortField
          brandKit={brandKit}
          element={element}
          form={form}
        />
      );
    case CustomFieldType.YesNoQuestion:
      return (
        <CustomYesNoField brandKit={brandKit} element={element} form={form} />
      );
    default:
      assertNever(element.customField.fieldType);
      return null;
  }
});

const componentMap: Record<
  InputElement["inputType"],
  NamedExoticComponent<FormElementProps<InputElement>>
> = {
  email: EmailInput,
  phone: PhoneInput,
  birthday: BirthdayInput,
  custom: CustomInput,
};

export const Input = memo(function Input(
  props: FormElementProps<InputElement>,
) {
  const { element } = props;

  const Component = componentMap[element.inputType];

  if (Component) {
    return <Component {...props} />;
  }

  return null;
});
